import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { listCategories, listPublishedArticles } from "@/lib/blog.functions";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const SITE = "https://heallexa.com";
const PAGE_SIZE = 24;

export const Route = createFileRoute("/blog/$category")({
  loader: async ({ params }) => {
    await setSeoPageCache();
    const cats = await listCategories();
    const cat = cats.find((c) => c.slug === params.category);
    if (!cat) throw notFound();
    const first = await listPublishedArticles({ data: { category_slug: params.category, limit: PAGE_SIZE, offset: 0 } });
    return { cat, cats, firstPage: first };
  },
  head: ({ loaderData }) => {
    const catName = loaderData?.cat?.name ?? "Healthcare";
    const catSlug = loaderData?.cat?.slug ?? "";
    const catDesc =
      loaderData?.cat?.description ??
      "Healthcare discovery guides from the Heallexa blog.";
    const url = `${SITE}/blog/${catSlug}`;
    const title = catName ? `${catName} | Heallexa Blog` : "Heallexa Blog";

    return {
      meta: [
        { title },
        { name: "description", content: catDesc },
        { property: "og:title", content: title },
        { property: "og:description", content: catDesc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description: catDesc,
            url,
            inLanguage: "en-US",
            isPartOf: {
              "@type": "Blog",
              name: "Heallexa Blog",
              url: `${SITE}/blog`,
              publisher: {
                "@type": "Organization",
                name: "Heallexa",
                url: SITE,
              },
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
                { "@type": "ListItem", position: 3, name: catName, item: url },
              ],
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background"><Navbar /><div className="pt-32 mx-auto max-w-3xl px-6 text-center"><h1 className="text-2xl font-semibold">Category not found</h1><Link to="/blog" className="text-primary underline mt-4 inline-block">Back to blog</Link></div></div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background"><Navbar /><div className="pt-32 mx-auto max-w-3xl px-6"><p className="text-destructive">{error.message}</p></div></div>
  ),
  component: BlogCategoryPage,
});

function BlogCategoryPage() {
 const { cat, cats, firstPage } = Route.useLoaderData();
 const [search, setSearch] = useState("");

 const { data: pages, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
 queryKey: ["blog-cat", cat.slug],
 initialPageParam: 0,
 queryFn: ({ pageParam }) =>
 listPublishedArticles({ data: { category_slug: cat.slug, limit: PAGE_SIZE, offset: pageParam as number } }),
 getNextPageParam: (last, all) => (last.hasMore ? all.length * PAGE_SIZE : undefined),
 initialData: { pages: [firstPage], pageParams: [0] },
 });

 const enriched = useMemo(() => {
 const all = (pages?.pages ?? []).flatMap((p: any) => p.rows ?? []);
 return all.map((a: any) => ({ ...a, category_name: cat.name }));
 }, [pages, cat.name]);

 const filtered = useMemo(() => {
 const q = search.trim().toLowerCase();
 if (!q) return enriched;
 return enriched.filter((a: any) =>
 a.title?.toLowerCase().includes(q) || a.excerpt?.toLowerCase().includes(q),
 );
 }, [enriched, search]);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />

 <header className="relative pt-28 pb-12 overflow-hidden border-b border-border">
 <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/10 to-transparent" />
 <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
 <div className="relative mx-auto max-w-6xl px-6">
 <Link to="/blog" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground">← Heallexa Blog</Link>
 <span className="mt-3 inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/10 text-primary">
 Category
 </span>
 <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mt-3">{cat.name}</h1>
 {cat.description && <p className="text-muted-foreground mt-3 max-w-2xl text-lg">{cat.description}</p>}
 </div>
 </header>

 <main className="mx-auto max-w-6xl px-6 py-12 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
 <BlogSidebar categories={cats} activeSlug={cat.slug} search={search} onSearchChange={setSearch} />

 <div className="min-w-0">
 <div className="flex items-end justify-between mb-5">
 <h2 className="text-2xl font-semibold tracking-tight">
 {search ? `Results for "${search}"` : "All articles"}
 </h2>
 <span className="text-xs text-muted-foreground">{filtered.length} article{filtered.length === 1 ? "" : "s"}</span>
 </div>

 {filtered.length === 0 ? (
 <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center text-sm text-muted-foreground">
 {search ? "No articles match your search." : "No articles in this category yet."}
 </div>
 ) : (
 <>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
 {filtered.map((a: any) => (
 <ArticleCard key={a.id} article={a} />
 ))}
 </div>
 {!search && hasNextPage && (
 <div className="flex justify-center mt-8">
 <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
 {isFetchingNextPage ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading\u2026</> : "Load more"}
 </Button>
 </div>
 )}
 </>
 )}
 </div>
 </main>

 <FooterSection />
 </div>
 );
}
